/* eslint-disable @typescript-eslint/no-use-before-define --
   The adapter class stays at the top of the file so the file's shape
   reads "here's the adapter, here are its private helpers below" —
   hoisted `function` declarations make that ordering safe at runtime. */
import type {
  WarmHandoffAdapter,
  WarmHandoffAdapterPreparation,
  WarmHandoffAdapterPreparationResult,
  WarmHandoffProjection,
} from "@leagueplatform/chat-contract";
import {
  clearGenesysConversation,
  startGenesysConversation,
  waitForNextConversationCleared,
  wipeGenesysSessionState,
} from "./genesys-messenger";
import {
  isOutsideHoursOfOperation,
  MAX_CONTEXT_ATTRIBUTE_BYTES,
  stripInternalConfigKeys,
  utf8Size,
} from "./live-chat-config";

// Provider key this adapter answers to; anything else is another vendor's
// handoff and must fail with `unsupported_provider`.
const GENESYS_PROVIDER = "genesys";

// How long preparation may take before the handoff is reported as timed
// out. Hard cap from the SDK's own coordinator is 10s
// (`warm-handoff-coordinator.ts:67 ADAPTER_OPERATION_TIMEOUT_MS`); anything
// at or above that races the coordinator's own timeout and cleanup gets
// truncated. 9500ms leaves a small margin below that cap so our own
// timeout wins the race and cleanup runs cleanly. Was 8000ms — bumped
// because Genesys' `.started` sometimes arrives later than that when the
// deployment's routing flow is slow (observed 8-9s in DEV runs), which
// made the adapter abandon before proof-of-delivery could settle.
const PREPARE_TIMEOUT_MS = 9_500;

// Ceiling for waiting on `conversationCleared` after a successful
// `clearConversation`. `clearGenesysConversation` resolves when Genesys'
// command bus accepts the command, not when the server-side clear
// completes; the confirmation event fires later. Observed: when the
// event lands *between* our subsequent `startConversation` dispatch and
// its FULFILLED, Genesys silently drops the start — no `.started`, no
// `.error`, adapter times out at PREPARE_TIMEOUT_MS. Awaiting the event
// before we dispatch startConversation avoids that overlap. 2s is
// generous headroom over observed timings (typically 100-500ms in the
// captures); if the event never comes, we fall through and let the
// prepare timeout handle it.
const CONVERSATION_CLEARED_WAIT_MS = 2_000;

// The byte budget (`MAX_CONTEXT_ATTRIBUTE_BYTES`) covers the whole staged
// map, so routing attributes and the projection are measured together.
// Over budget fails the handoff rather than truncating, because a
// truncated projection is no longer the one the server approved — unlike
// the ordinary chat start, which keeps the routing attributes.

const LIST_SEPARATOR = "; ";

const ATTRIBUTE_CURRENT_NEED = "warmTransferCurrentNeed";
const ATTRIBUTE_SUMMARY = "warmTransferSummary";
const ATTRIBUTE_STEPS_TAKEN = "warmTransferStepsTaken";
const ATTRIBUTE_OPEN_QUESTIONS = "warmTransferOpenQuestions";
const ATTRIBUTE_DO_NOT_REPEAT = "warmTransferDoNotRepeat";

const TIMEOUT_MARKER = "GENESYS_WARM_HANDOFF_TIMEOUT";

// Marker prefix on errors thrown from `runPreparation` for expected user
// states (no configs / HOO closed / attribute budget exceeded). The
// `prepare` catch inspects this to suppress console.error noise — these
// are gate outcomes, not error conditions.
const PREFLIGHT_ERROR_PREFIX = "PREFLIGHT:";

// Marker on errors thrown from `runPreparation` when the outer
// `withTimeout` has already fired (or a newer prepare superseded this
// one) and the pipeline detects the generation mismatch. Also silent in
// the catch — the outer withTimeout has already surfaced adapter_timeout
// to the caller; this is just the pipeline unwinding cooperatively.
const CANCELLED_MARKER = "GENESYS_WARM_HANDOFF_CANCELLED";

export interface GenesysWarmHandoffAdapterOptions {
  /**
   * Fetches Emblem's live-chat config (routing attributes + hours-of-
   * operation gate). Ordinary path collects them at pre-chat; a handoff
   * skips pre-chat, so the adapter fetches them itself.
   */
  getLiveChatConfigs: () => Promise<Record<string, unknown> | undefined>;
  /**
   * True when a Genesys conversation is already live (the member is
   * mid-chat with an agent). iOS parity: `GenesysWarmHandoffAdapter`
   * refuses to `prepare` in this state so a handoff can't kill an
   * active human conversation.
   */
  hasActiveSession: () => boolean;
  /**
   * Called at the START of `prepare`. Signals the bridge to start
   * buffering inbound Genesys events — Genesys' bot may auto-respond
   * (and disconnect) before the SDK's session-machine has swapped to
   * appClient, and any bridge emit during that window would flow to a
   * V2 event stream with no active subscriber. Buffer holds them until
   * `onHandoffCommitted` / `onHandoffAbandoned` fires.
   */
  onHandoffStarted?: () => void;
  /**
   * Called from `cancel` or on any `prepare` failure. Signals the bridge
   * to DROP the buffered events without forwarding — the Genesys
   * conversation was abandoned, its bot messages don't belong to any
   * SDK-visible chat.
   */
  onHandoffAbandoned?: () => void;
  /**
   * Arms the bridge's preparation-outcome latch before
   * `startGenesysConversation` fires, so signals delivered during the
   * command aren't missed. Called from `prepare` immediately after
   * `onHandoffStarted`.
   */
  beginPreparationTracking?: () => void;
  /**
   * Records the attributes the adapter is about to hand to
   * `startGenesysConversation`. Called BEFORE the command so the bridge
   * can replay them on its readOnly-recovery terminate+restart path.
   * Without this the recovery restart fires with `customAttrs=0` and
   * the `warmTransfer*` context is dropped on the floor — the agent
   * picks up the chat with no handoff summary. Only fires when
   * `resolved: true` — a rejected preparation still calls this so a
   * subsequent recovery isn't stuck with stale attributes from a prior
   * handoff attempt.
   */
  noteStartAttributes?: (attributes: Record<string, unknown>) => void;
  /**
   * Resolves when Genesys confirms the conversation is up with the
   * staged attributes; rejects on `customAttributesSizeExceeded`. iOS
   * parity approximation of `Event.ConversationAutostart`. Only called
   * after `startGenesysConversation` fulfils.
   */
  awaitPreparationDelivered?: () => Promise<void>;
  prepareTimeoutMs?: number;
}

type AcceptedPreparation = { readonly warmHandoffId: string };

/**
 * Delivers an AI conversation's approved context onto a Genesys
 * conversation. Genesys cannot separate "create the session" from
 * "attach the context" — attributes staged before the socket is up are
 * dispatched with the autostart, so preparation *is* the conversation:
 * it is live, and possibly already queued to an agent, before the SDK's
 * `activate` call arrives. Activation only signals the handover.
 */
export class GenesysWarmHandoffAdapter implements WarmHandoffAdapter {
  private readonly getLiveChatConfigs: () => Promise<
    Record<string, unknown> | undefined
  >;

  private readonly hasActiveSession: () => boolean;

  private readonly onHandoffStarted?: () => void;

  private readonly onHandoffAbandoned?: () => void;

  private readonly beginPreparationTracking?: () => void;

  private readonly noteStartAttributes?: (
    attributes: Record<string, unknown>
  ) => void;

  private readonly awaitPreparationDelivered?: () => Promise<void>;

  private readonly prepareTimeoutMs: number;

  private accepted: AcceptedPreparation | undefined;

  // Monotonically bumped on every `prepare` invocation and on any
  // timeout/reject in the catch block. `runPreparation` snapshots this
  // at entry and re-checks between every awaited side effect; a
  // generation mismatch means the outer `prepare` has already timed
  // out (or been superseded by a newer prepare) and the pipeline must
  // abort — including tearing down any Genesys conversation
  // `startGenesysConversation` may have just opened after the outer
  // withTimeout already fired. Without this, `withTimeout` only races
  // the promise; the underlying transport work keeps running and can
  // create an orphaned conversation after Eva reported failure.
  private preparationGeneration = 0;

  constructor(options: GenesysWarmHandoffAdapterOptions) {
    this.getLiveChatConfigs = options.getLiveChatConfigs;
    this.hasActiveSession = options.hasActiveSession;
    this.onHandoffStarted = options.onHandoffStarted;
    this.onHandoffAbandoned = options.onHandoffAbandoned;
    this.beginPreparationTracking = options.beginPreparationTracking;
    this.noteStartAttributes = options.noteStartAttributes;
    this.awaitPreparationDelivered = options.awaitPreparationDelivered;
    this.prepareTimeoutMs = options.prepareTimeoutMs ?? PREPARE_TIMEOUT_MS;
  }

  async prepare(
    preparation: WarmHandoffAdapterPreparation
  ): Promise<WarmHandoffAdapterPreparationResult> {
    if (preparation.provider.toLowerCase() !== GENESYS_PROVIDER) {
      return { status: "failed", failureCode: "unsupported_provider" };
    }

    // iOS parity (`GenesysWarmHandoffAdapter.swift:48`): refuse to prep
    // while the member is already in a live conversation. Opening a new
    // Genesys conversation would close the current one — dropping the
    // member from a chat with an agent they're actively talking to.
    if (this.hasActiveSession()) {
      // eslint-disable-next-line no-console
      console.error(
        "[genesys-warm-handoff-adapter] prepare refused: member already in a live Genesys conversation"
      );
      return { status: "failed", failureCode: "adapter_failed" };
    }

    // Start buffering inbound Genesys events before any transport call —
    // Genesys can begin routing / auto-response as soon as the WS is up.
    this.onHandoffStarted?.();

    this.preparationGeneration += 1;
    const generation = this.preparationGeneration;

    try {
      // Wrap the ENTIRE preparation pipeline (config fetch → HOO check →
      // attribute build → pre-clear → wipe → start → delivery-await,
      // including the bridge's clear + restart when Genesys resumes the
      // member's old conversation) in one wall-clock budget. Prior versions only
      // bounded the startConversation stage, so a hanging config fetch
      // or clear could exceed the SDK coordinator's 10s ceiling and
      // orphan the handoff. Keeping the whole pipeline under
      // `prepareTimeoutMs` (default 9_500ms) ensures our own timeout
      // wins the race against the coordinator's 10s abandon.
      await withTimeout(
        this.runPreparation(preparation, generation),
        this.prepareTimeoutMs
      );
    } catch (err) {
      // Bump the generation immediately so any in-flight side effect
      // inside `runPreparation` (config fetch resolving late, or an
      // already-dispatched `startConversation` resolving after our
      // withTimeout fired) sees the mismatch and self-tears-down
      // instead of creating an orphaned Genesys conversation after we
      // reported failure.
      this.preparationGeneration += 1;
      const isTimeout = err instanceof Error && err.message === TIMEOUT_MARKER;
      const isPreflight =
        err instanceof Error && err.message.startsWith(PREFLIGHT_ERROR_PREFIX);
      const isCancelled =
        err instanceof Error && err.message === CANCELLED_MARKER;
      // Preflight failures (no configs / HOO closed / attribute budget
      // exceeded) and cooperative cancellations (outer withTimeout fired
      // and runPreparation detected the generation mismatch) are
      // expected states, not error conditions — stay silent to avoid
      // noise. Timeout and unexpected runtime rejects log for
      // diagnostics (message text only in DEV per
      // Wiz WS-JAVASCRIPT-01076).
      if (!isPreflight && !isCancelled) {
        // eslint-disable-next-line no-console
        console.error(
          `[genesys-warm-handoff-adapter] prepare ${
            isTimeout ? "TIMED OUT" : "REJECTED"
          } in preparation pipeline`
        );
        if (import.meta.env.DEV) {
          const errMessage =
            err instanceof Error ? `${err.name}: ${err.message}` : String(err);
          // eslint-disable-next-line no-console
          console.debug(
            "[genesys-warm-handoff-adapter] prepare error detail (DEV only):",
            errMessage
          );
        }
      }
      // Notify the bridge FIRST so the handoff buffer is discarded and
      // the coordinator's 10s cleanup budget starts unblocking, even if
      // the transport is wedged. `clearGenesysConversation` goes through
      // cxbus which is uncapped — if Genesys' SDK is uninitialized or
      // wedged, awaiting it could hang past the coordinator's window,
      // stranding the buffer. Fire-and-forget the cleanup instead: a
      // failed preparation may have left a conversation attached, so
      // ending it is still worth trying, but not at the cost of the
      // abandon signal.
      this.onHandoffAbandoned?.();
      clearGenesysConversation().catch(() => undefined);
      return {
        status: "failed",
        failureCode: isTimeout ? "adapter_timeout" : "adapter_failed",
      };
    }

    this.accepted = { warmHandoffId: preparation.warmHandoffId };
    return { status: "accepted" };
  }

  private async runPreparation(
    preparation: WarmHandoffAdapterPreparation,
    generation: number
  ): Promise<void> {
    // Cooperative cancellation. `withTimeout` in the caller only races
    // the promise; it doesn't propagate an abort signal into the async
    // work below. When the outer withTimeout fires (or a newer prepare
    // supersedes this one), the caller bumps `preparationGeneration` —
    // this check between every side effect stops the pipeline before
    // it opens a Genesys conversation the caller no longer expects.
    const isStale = () => generation !== this.preparationGeneration;
    const bailIfStale = () => {
      if (isStale()) throw new Error(CANCELLED_MARKER);
    };

    const configs = await this.getLiveChatConfigs().catch(() => undefined);
    bailIfStale();
    if (!configs) throw new Error(`${PREFLIGHT_ERROR_PREFIX}no-configs`);
    if (isOutsideHoursOfOperation(configs)) {
      throw new Error(`${PREFLIGHT_ERROR_PREFIX}hoo-closed`);
    }

    const attributes = contextAttributes(configs, preparation.projection);
    if (!attributes) {
      throw new Error(`${PREFLIGHT_ERROR_PREFIX}attributes-over-budget`);
    }

    // Best-effort clean of any lingering server-side conversation before
    // opening one for the handoff. Matches the bridge's unconditional
    // pre-clear on START_CHAT — Genesys otherwise rejects `startConversation`
    // with "There is already an active conversation" when a prior session
    // was left attached.
    //
    // Arm the `conversationCleared` waiter BEFORE dispatching the clear
    // so the event isn't missed if it fires between dispatch resolve and
    // us starting to listen. When the clear actually did tear something
    // down, wait for the server-side confirmation before proceeding —
    // that's the race that silently kills `startConversation` (see the
    // note on CONVERSATION_CLEARED_WAIT_MS). When the clear rejected
    // (nothing to clear), the event isn't coming, so skip the wait.
    bailIfStale();
    const clearedWaiter = waitForNextConversationCleared(
      CONVERSATION_CLEARED_WAIT_MS
    );
    const clearFulfilled = await clearGenesysConversation()
      .then(() => true)
      .catch(() => false);
    if (clearFulfilled) {
      await clearedWaiter;
    }
    bailIfStale();

    // Drop the Conversations plugin's continuation key so Genesys can't
    // resume onto a disconnected conversation from a prior session —
    // that path presents as `readOnly: true` on `.started` and every
    // send returns 409. Mirrors iOS `GenesysCloudMessenger.startSeparateSession`,
    // which removes the session token from the vault on the warm-handoff
    // path for the same reason.
    //
    // This only clears keys on our top-frame origin. The SDK's real
    // session tokens live in Genesys' cross-origin iframe storage
    // (`apps.usw2.pure.cloud`), which our JS cannot touch directly, and
    // the pre-clear above is refused ("no active conversation") until
    // the socket has resumed the old conversation. So Genesys can still
    // resume the member's unfinished chat on start (`.started` with
    // `newSession: false`, then `.restored`); the bridge answers that by
    // clearing the conversation and the session, then restarting with
    // these attributes — parity with mobile's clear + separate session. `Database.clear` is not an
    // option: Genesys refuses it ("Genesys plugin does not have
    // permission to access clear Database plugin command").
    wipeGenesysSessionState();

    // Arm the bridge's preparation-outcome latch BEFORE calling
    // startGenesysConversation so a `.started` / `.customAttributesSizeExceeded`
    // signal that fires during the command can't slip past the
    // `awaitPreparationDelivered` window that opens right after.
    this.beginPreparationTracking?.();
    // Stash the attributes on the bridge so its readOnly-recovery path
    // (terminate + restart) can re-apply them if Genesys resumes a
    // prior disconnected conversation as `readOnly:true`. Without this,
    // the recovery restart fires with `undefined` attrs and the
    // `warmTransfer*` context is lost — the agent picks up the chat
    // cold. See `emblem-live-chat-client.ts:noteWarmHandoffAttributes`.
    this.noteStartAttributes?.(attributes);

    // iOS parity approximation of `Event.ConversationAutostart`
    // (`GenesysCloudMessenger.swift:593-599`): don't return `accepted`
    // until Genesys confirms the conversation is up with the staged
    // attributes. The delivery-await rejects on
    // `customAttributesSizeExceeded` (Genesys dropped the WHOLE map,
    // agent would pick the chat up cold).
    await startGenesysConversation(attributes);
    // Post-start stale check: this is the failure Copilot F-High flagged.
    // If the outer withTimeout fired between the `startGenesysConversation`
    // dispatch and its fulfillment, the caller has already reported
    // `adapter_timeout` and moved on — but a Genesys conversation was
    // actually opened. Tear it down immediately so we don't orphan it.
    if (isStale()) {
      await clearGenesysConversation().catch(() => undefined);
      throw new Error(CANCELLED_MARKER);
    }
    if (this.awaitPreparationDelivered) {
      await this.awaitPreparationDelivered();
    }
    bailIfStale();
  }

  async activate(warmHandoffId: string): Promise<void> {
    const { accepted } = this;
    if (!accepted || accepted.warmHandoffId !== warmHandoffId) {
      throw new Error(
        "GenesysWarmHandoffAdapter.activate: no accepted preparation for this id"
      );
    }
    // Nothing more to do vendor-side; the conversation has been live
    // since `prepare()`. The SDK's coordinator promotes the session it
    // reserved against this adapter.
  }

  async cancel(warmHandoffId: string): Promise<void> {
    const { accepted } = this;
    if (!accepted || accepted.warmHandoffId !== warmHandoffId) return;
    this.accepted = undefined;
    this.onHandoffAbandoned?.();
    // Idempotent — Genesys throws when the client is already closed,
    // which is the case we want to no-op on.
    await clearGenesysConversation().catch(() => undefined);
  }
}

// ---------------------------------------------------------------------
// Attribute translation + budget
// ---------------------------------------------------------------------

/**
 * The projection and Emblem's routing attributes as one map, or `undefined`
 * when together they exceed the byte budget. Routing goes on last: the
 * Architect flow branches on those keys, so a projection that reused one
 * must not displace it. `LIVE_CHAT_AVAILABLE` is dropped for the same
 * reason the ordinary path drops it — transport state, not context an
 * agent should see.
 */
function contextAttributes(
  configs: Record<string, unknown>,
  projection: WarmHandoffProjection
): Record<string, unknown> | undefined {
  // `stripInternalConfigKeys` drops every routing key the SDK owns
  // (LIVE_CHAT_AVAILABLE, DEFAULT_PROVIDER, ...) — matches what the
  // bubble path emits to Genesys, so warm-handoff and USER_START_CHAT
  // send the same attribute surface to agents.
  const merged: Record<string, unknown> = {
    ...projectionToAttributes(projection),
    ...(stripInternalConfigKeys(configs) ?? {}),
  };
  if (utf8Size(merged) > MAX_CONTEXT_ATTRIBUTE_BYTES) return undefined;
  return merged;
}

/**
 * Lists are joined into one value each rather than exploded into per-item
 * keys — every key costs budget, and an empty list is left out entirely
 * for the same reason.
 */
function projectionToAttributes(
  projection: WarmHandoffProjection
): Record<string, string> {
  const out: Record<string, string> = {
    [ATTRIBUTE_CURRENT_NEED]: projection.currentNeed,
    [ATTRIBUTE_SUMMARY]: projection.summary,
  };
  const stepsTaken = joinedOrEmpty(projection.stepsAlreadyTaken);
  if (stepsTaken) out[ATTRIBUTE_STEPS_TAKEN] = stepsTaken;
  const openQuestions = joinedOrEmpty(projection.openQuestions);
  if (openQuestions) out[ATTRIBUTE_OPEN_QUESTIONS] = openQuestions;
  const doNotRepeat = joinedOrEmpty(projection.doNotRepeat);
  if (doNotRepeat) out[ATTRIBUTE_DO_NOT_REPEAT] = doNotRepeat;
  return out;
}

function joinedOrEmpty(list: readonly string[]): string | undefined {
  return list.length > 0 ? list.join(LIST_SEPARATOR) : undefined;
}

// ---------------------------------------------------------------------
// Timeout helper
// ---------------------------------------------------------------------

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(TIMEOUT_MARKER)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}
